// @flow
import React from 'react';
import { connect } from 'react-redux';
import FullHeightSearchPanel from 'app/common/react/components/profile/ProfileSearch/FullHeightSearchPanel';
import i18n from 'app/common/lib/i18n';
import NumberUtil from 'app/common/lib/NumberUtil';
import ProfileProductPins from 'app/common/react/components/profile/storefront/ProfileProductPins/ProfileProductPins';
import ProfileSearchPins from 'app/common/react/components/profile/ProfileSearch/ProfileSearchPins';
import SectionHeader from 'app/common/react/components/profile/SectionHeader/SectionHeader';
import type { ProfileUser } from 'app/packages/pinterest-flow-types/user';
import type { Viewer } from 'app/packages/pinterest-flow-types/viewer';
import withResource from 'app/common/lib/react/withResource';
import withViewer from 'app/common/lib/react/withViewer';
import { SingleTreatmentExp } from 'app/common/lib/experiment';
import { type State as ReduxState } from 'app/common/redux/reducers/types';
import type { Category, Filter } from 'app/common/react/components/profile/ProfileSearch/types';
import {
  CancelSearch,
  CloseSearchPanel,
  openSearchPanel,
  updateDisplayFilter,
} from 'app/common/redux/actions/storefrontSearch';
import {
  StorefrontSearchSortTypeNameMap,
  StorefrontSearchSortTypeDefaultValue,
} from 'app/common/react/components/profile/storefront/Filter/constants';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';
import { Box, IconButton, Text, Touchable } from 'gestalt';
import { compose, type Dispatch } from 'redux';

type OwnProps = {|
  allProductPins: {
    data: Array<Object>,
    fetchMore: () => void,
    isFetching: boolean,
    isLoaded: boolean,
    refreshData: () => void,
  },
  isOwnProfile: boolean,
  requestContext: RequestContextType,
  searchResource: {
    data: {
      categories: Array<Category>,
      filter: Filter,
      results: Array<Object>,
    },
    fetchMore: () => void,
    isFetching: boolean,
    isLoaded: boolean,
    refreshData: () => void,
  },
  user: ProfileUser,
  viewer: Viewer,
|};

type ReduxStateProps = {|
  appliedFilter: Filter,
  appliedQueryText: string,
  appliedSortBy: string,
  displayFilterIsSet: boolean,
  inSearchMode: boolean,
  showSearchPanel: boolean,
|};

type ReduxDispatchProps = {|
  cancelSearch: () => void,
  closeSearchPanel: () => void,
  openSearchPanel: (boolean) => void,
  updateDisplayFilter: (Filter) => void,
|};

type Props = {| ...OwnProps, ...ReduxStateProps, ...ReduxDispatchProps |};

const MIN_WIDTH = 1030;
const SCROLL_OFFSET = 200; // Height of Header + Tab bar

class ProfileAllProductPins extends React.PureComponent<Props> {
  ref: { current: null | HTMLDivElement } = React.createRef();

  componentDidMount(): void {
    const { searchResource } = this.props;
    const filter =
      searchResource.data && searchResource.data.filter ? searchResource.data.filter : {};
    if (filter && filter.price) {
      this.updateDisplayFilter(filter);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { searchResource, displayFilterIsSet } = this.props;
    const filter =
      searchResource.data && searchResource.data.filter ? searchResource.data.filter : {};
    const prevFilter =
      prevProps.searchResource.data && prevProps.searchResource.data.filter
        ? prevProps.searchResource.data.filter
        : {};
    if (
      !displayFilterIsSet &&
      filter &&
      filter.price &&
      (!prevFilter.price || prevFilter.price !== filter.price)
    ) {
      // filters returned from searchResource have changed
      this.updateDisplayFilter(filter);
    }
  }

  handleCloseSearch = () => {
    this.props.cancelSearch();
    this.props.closeSearchPanel();
  };

  updateDisplayFilter = (filter: Filter) => {
    const filterInMicroCurrency = {
      ...filter,
      price: {
        max: filter.price ? NumberUtil.currencyToMicroCurrency(filter.price.max) : 0,
        min: filter.price ? NumberUtil.currencyToMicroCurrency(filter.price.min) : 0,
      },
    };
    this.props.updateDisplayFilter(filterInMicroCurrency);
  };

  scrollToTopOfGrid = () => {
    if (this.ref.current && this.ref.current.offsetTop > SCROLL_OFFSET) {
      window.scrollTo(0, this.ref.current.offsetTop - SCROLL_OFFSET);
    }
  };

  renderSearchButton = () => {
    const { appliedQueryText, inSearchMode } = this.props;
    const displayText =
      appliedQueryText.length > 20 ? appliedQueryText.substring(0, 20) + '...' : appliedQueryText;
    return (
      <Box display="flex" alignItems="center">
        <Box display="flex" alignItems="center" color="lightGray" shape="rounded">
          <Box flex="none">
            <IconButton
              onClick={() => this.props.openSearchPanel(true)}
              icon="search"
              size="lg"
              accessibilityLabel={i18n._(
                'open search panel',
                'accessibility label for search button',
              )}
            />
          </Box>
          {inSearchMode &&
            displayText && (
              <Box marginLeft={1} flex="grow">
                <Touchable onTouch={() => this.props.openSearchPanel(true)}>
                  <Text size="lg"> {displayText} </Text>
                </Touchable>
              </Box>
            )}
          {inSearchMode &&
            displayText && (
              <Box marginLeft={3} flex="none">
                <IconButton
                  accessibilityLabel={i18n._(
                    'cancel search',
                    'accessibility label for cancel search button',
                  )}
                  onClick={this.handleCloseSearch}
                  icon="cancel"
                  size="sm"
                />
              </Box>
            )}
        </Box>
        <Box color="lightGray" shape="rounded" marginLeft={2}>
          <IconButton
            onClick={() => this.props.openSearchPanel(false)}
            icon="filter"
            size="lg"
            accessibilityLabel={i18n._(
              'open search panel',
              'accessibility label for filter button',
            )}
          />
        </Box>
      </Box>
    );
  };

  render() {
    const {
      allProductPins,
      inSearchMode,
      isOwnProfile,
      requestContext,
      searchResource,
      showSearchPanel,
      user,
      viewer,
    } = this.props;
    const shouldShowSearchButtons =
      user &&
      user.storefront_search_enabled &&
      new SingleTreatmentExp(requestContext, 'web_bizpro_product_search').enabled();
    return (
      <Box ref={this.ref}>
        <Box direction="row" position="relative" marginBottom={2}>
          <Box display="flex" justifyContent="center">
            <SectionHeader
              label={i18n._('All Products', 'label for all products linked to profile')}
            />
          </Box>
          <Box flex="shrink" position="absolute" right top marginRight={6}>
            {shouldShowSearchButtons && this.renderSearchButton()}
          </Box>
        </Box>
        <Box minWidth={MIN_WIDTH} width="100%">
          {inSearchMode ? (
            <ProfileSearchPins
              isOwnProfile={isOwnProfile}
              user={user}
              viewer={viewer}
              searchResource={searchResource}
            />
          ) : (
            <ProfileProductPins
              isOwnProfile={isOwnProfile}
              pins={allProductPins}
              user={user}
              viewer={viewer}
              productGroupType={'ALL_PRODUCTS'}
              isGridCentered
            />
          )}
          {/*Add a empty space here such that the window would not jump up while the grid is still loading */}
          <Box height={window.innerHeight} />
        </Box>
        <Box>
          {showSearchPanel && <FullHeightSearchPanel handleSearchSubmit={this.scrollToTopOfGrid} />}
        </Box>
      </Box>
    );
  }
}

const mapStateToProps = ({ storefrontSearch }: ReduxState): ReduxStateProps => ({
  appliedFilter: storefrontSearch.currentSearch.filter || {},
  appliedQueryText: storefrontSearch.currentSearch.queryText || '',
  appliedSortBy: storefrontSearch.currentSearch.sortBy || '',
  displayFilterIsSet: storefrontSearch.displayFilterIsSet,
  inSearchMode: storefrontSearch.inSearchMode,
  showSearchPanel: storefrontSearch.showSearchPanel,
});

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  cancelSearch: () => dispatch(CancelSearch),
  closeSearchPanel: () => dispatch(CloseSearchPanel),
  openSearchPanel: (inputFocus: boolean) => dispatch(openSearchPanel(inputFocus)),
  updateDisplayFilter: (filter: Filter) => dispatch(updateDisplayFilter(filter)),
});

export default compose(
  withRequestContext,
  withViewer,
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withResource({
    name: 'StoreFrontSearchResource',
    key: 'searchResource',
    options: (props) => {
      const sortByValue = props.appliedSortBy || StorefrontSearchSortTypeDefaultValue;
      const sortTypeParam = StorefrontSearchSortTypeNameMap[sortByValue];
      const filterInWholeCurrency =
        props.appliedFilter && props.appliedFilter.price
          ? Object.assign({}, props.appliedFilter, {
              price: {
                max: NumberUtil.microCurrencyToCurrency(props.appliedFilter.price.max),
                min: NumberUtil.microCurrencyToCurrency(props.appliedFilter.price.min),
              },
            })
          : props.appliedFilter;
      return {
        username: props.user.username,
        user_id: props.user.id,
        filter: filterInWholeCurrency,
        query: props.appliedQueryText,
        sort_type: sortTypeParam,
        field_set_key: 'grid_item',
      };
    },
  }),
)(ProfileAllProductPins);
